import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Swords, Zap, Loader2, X, Coins, Trophy, Home, Volume2, VolumeX, RotateCcw, Share2, Link as LinkIcon } from 'lucide-react';
import { initAudio, startMusic, stopMusic, setMuted, playSfx, setMusicTheme } from '../lib/seatAudio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = '/almanac/marketing/seat-fighter-logo.png';

const getPlayerId = () => {
  let id = localStorage.getItem('seat_player_id');
  if (!id) {
    id = 'sf_' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
    localStorage.setItem('seat_player_id', id);
  }
  return id;
};

const TallyBar = ({ a, b, colorA, colorB }) => {
  const total = a + b;
  const pa = total === 0 ? 50 : Math.round((a / total) * 100);
  return (
    <div className="w-full">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-black/60 border border-white/10" data-testid="tally-bar">
        <motion.div animate={{ width: `${pa}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} style={{ background: colorA }} className="h-full" />
        <motion.div animate={{ width: `${100 - pa}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} style={{ background: colorB }} className="h-full" />
      </div>
      <div className="flex justify-between mt-1 text-[11px] font-bold">
        <span style={{ color: colorA }}>{pa}% · {a}</span>
        <span style={{ color: colorB }}>{100 - pa}% · {b}</span>
      </div>
    </div>
  );
};

const PowerButton = ({ power, color, selected, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left rounded-xl border px-3 py-2.5 transition-all ${
      selected ? 'scale-[1.02] shadow-lg' : 'hover:border-white/30'
    }`}
    style={{
      borderColor: selected ? color : 'rgba(255,255,255,0.12)',
      background: selected ? `${color}22` : 'rgba(0,0,0,0.35)',
    }}
  >
    <div className="flex items-center gap-1.5">
      <Zap className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <span className="font-bold text-sm text-white leading-tight">{power.name}</span>
    </div>
    <p className="text-[11px] text-white/60 mt-1 leading-snug">{power.desc}</p>
  </button>
);

const C = (p) => ({ idle: `/almanac/seatfighter/${p}-idle.png`, atk: `/almanac/seatfighter/${p}-atk.png`, hurt: `/almanac/seatfighter/${p}-hurt.png` });
const CUTOUT = {
  'dipke-vs-modi': {
    a: { idle: '/almanac/seatfighter/dipke-stance.png', atk: '/almanac/seatfighter/dipke-atk.png', hurt: '/almanac/seatfighter/dipke-hurt.png' },
    b: { idle: '/almanac/seatfighter/modi-stance.png', atk: '/almanac/seatfighter/modi-atk.png', hurt: '/almanac/seatfighter/modi-hurt.png' },
  },
  'dhruv-vs-gyanu': { a: C('dhruv'), b: C('gyanu') },
  'reporters-vs-raf': { a: C('reporters'), b: C('raf') },
  'musk-vs-ambani': { a: C('musk'), b: C('ambani') },
  'budget-vs-middleclass': { a: C('budget'), b: C('mclass') },
};
const STAGE_BG = {
  'dipke-vs-modi': '/almanac/marketing/seat-stage-jantarmantar.jpg',
  'dhruv-vs-gyanu': '/almanac/marketing/seat-stage-dhruv-vs-gyanu.jpg',
  'reporters-vs-raf': '/almanac/marketing/seat-stage-reporters-vs-raf.jpg',
  'musk-vs-ambani': '/almanac/marketing/seat-stage-musk-vs-ambani.jpg',
  'budget-vs-middleclass': '/almanac/marketing/seat-stage-budget-vs-middleclass.jpg',
};
const stageBg = (slug) => STAGE_BG[slug] || '/almanac/marketing/seat-stage.png';
const poseSet = (slug, side, f) => {
  const c = CUTOUT[slug] && CUTOUT[slug][side];
  if (c) return { ...c, cut: true };
  const base = f && f.image_url ? f.image_url : '';
  return { idle: base, atk: base.replace(/\.png$/, '-attack.png'), hurt: base.replace(/\.png$/, '-hurt.png'), cut: false };
};

const P = (n) => `/almanac/seatfighter/props/${n}.png`;
// Signature move: motion + abstract vfx + a literal comic PROP (object) that drops/rains/flies onto the foe
const MOVES = {
  'dipke-vs-modi:a:p1': { name: 'Cockroach Swarm Rush', color: '#22d3ee', motion: 'dash', vfx: 'swarm', react: 'knockback', prop: 'cockroach', pstyle: 'rain' },
  'dipke-vs-modi:a:p2': { name: 'Youthquake Uppercut', color: '#38bdf8', motion: 'leap', vfx: 'uppercut', react: 'launch' },
  'dipke-vs-modi:a:p3': { name: 'Manifesto Shield', color: '#67e8f9', motion: 'guard', vfx: 'papers', react: 'knockback', prop: 'scroll', pstyle: 'shield' },
  'dipke-vs-modi:b:p1': { name: '56" Chestplate', color: '#f59e0b', motion: 'cast', vfx: 'chestplate', react: 'knockback', prop: 'chestplate', pstyle: 'drop' },
  'dipke-vs-modi:b:p2': { name: 'Mann Ki Baat Soundwave', color: '#fbbf24', motion: 'cast', vfx: 'soundwave', react: 'doze', prop: 'mic', pstyle: 'throw' },
  'dipke-vs-modi:b:p3': { name: 'Hologram Swarm', color: '#fde047', motion: 'multiply', vfx: 'hologram', react: 'knockback' },

  'dhruv-vs-gyanu:a:p1': { name: 'Fact-Check Fireball', color: '#38bdf8', motion: 'cast', vfx: 'fireball', react: 'knockback' },
  'dhruv-vs-gyanu:a:p2': { name: '20-Minute Explainer Trap', color: '#60a5fa', motion: 'cast', vfx: 'fog', react: 'doze', prop: 'clock', pstyle: 'drop' },
  'dhruv-vs-gyanu:a:p3': { name: 'Thumbnail Arrow Barrage', color: '#38bdf8', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'arrow', pstyle: 'rain' },
  'dhruv-vs-gyanu:b:p1': { name: 'Whataboutery Counter', color: '#fb7185', motion: 'guard', vfx: 'wall', react: 'knockback' },
  'dhruv-vs-gyanu:b:p2': { name: 'IT Cell Clone Army', color: '#fb7185', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'phone', pstyle: 'rain' },
  'dhruv-vs-gyanu:b:p3': { name: 'Forwarded-as-Received Smokescreen', color: '#f43f5e', motion: 'cast', vfx: 'fog', react: 'doze', prop: 'teargas', pstyle: 'cloud' },

  'reporters-vs-raf:a:p1': { name: 'Live-Feed Multiplication', color: '#a78bfa', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'camera', pstyle: 'rain' },
  'reporters-vs-raf:a:p2': { name: 'Mic-Boom Lance', color: '#a78bfa', motion: 'dash', vfx: 'none', react: 'launch', prop: 'mic', pstyle: 'throw' },
  'reporters-vs-raf:a:p3': { name: "'Exclusive' Flashbang", color: '#c4b5fd', motion: 'cast', vfx: 'flash', react: 'doze', prop: 'camera', pstyle: 'throw' },
  'reporters-vs-raf:b:p1': { name: 'Lathi Deflect Wall', color: '#34d399', motion: 'guard', vfx: 'none', react: 'knockback', prop: 'lathi', pstyle: 'shield' },
  'reporters-vs-raf:b:p2': { name: 'Tear-Gas Fog', color: '#34d399', motion: 'cast', vfx: 'fog', react: 'doze', prop: 'teargas', pstyle: 'cloud' },
  'reporters-vs-raf:b:p3': { name: 'Barricade Bulwark', color: '#10b981', motion: 'guard', vfx: 'none', react: 'knockback', prop: 'barricade', pstyle: 'shield' },

  'musk-vs-ambani:a:p1': { name: 'Starlink Orbital Strike', color: '#60a5fa', motion: 'cast', vfx: 'none', react: 'launch', prop: 'satellite', pstyle: 'rain' },
  'musk-vs-ambani:a:p2': { name: 'Midnight Tweet Nuke', color: '#60a5fa', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'bomb', pstyle: 'throw' },
  'musk-vs-ambani:a:p3': { name: 'Rocket Dash', color: '#93c5fd', motion: 'dash', vfx: 'none', react: 'knockback', prop: 'rocket', pstyle: 'throw' },
  'musk-vs-ambani:b:p1': { name: 'Jio Free-Data Tsunami', color: '#f472b6', motion: 'cast', vfx: 'wave', react: 'knockback', prop: 'sim', pstyle: 'rain' },
  'musk-vs-ambani:b:p2': { name: 'Antilia Fortress', color: '#f472b6', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'skyscraper', pstyle: 'drop' },
  'musk-vs-ambani:b:p3': { name: 'Wedding Guest-List Gravity Well', color: '#ec4899', motion: 'cast', vfx: 'tornado', react: 'knockback' },

  'budget-vs-middleclass:a:p1': { name: 'Taxation Tornado', color: '#fbbf24', motion: 'cast', vfx: 'tornado', react: 'knockback', prop: 'coin', pstyle: 'rain' },
  'budget-vs-middleclass:a:p2': { name: 'Cess Stacker', color: '#f59e0b', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'coin', pstyle: 'drop' },
  'budget-vs-middleclass:a:p3': { name: 'Fine-Print Phantom', color: '#fcd34d', motion: 'cast', vfx: 'none', react: 'doze', prop: 'document', pstyle: 'rain' },
  'budget-vs-middleclass:b:p1': { name: 'EMI Shield', color: '#2dd4bf', motion: 'guard', vfx: 'none', react: 'knockback', prop: 'card', pstyle: 'shield' },
  'budget-vs-middleclass:b:p2': { name: 'SIP Counter', color: '#2dd4bf', motion: 'cast', vfx: 'none', react: 'knockback', prop: 'chart', pstyle: 'grow' },
  'budget-vs-middleclass:b:p3': { name: "'Save Tax' Dodge", color: '#5eead4', motion: 'dash', vfx: 'none', react: 'knockback', prop: 'document', pstyle: 'throw' },
};

// Full-stage effects layer, keyed by fx.tick so each cast replays cleanly
const MoveFX = ({ fx }) => {
  if (!fx.on) return null;
  const fromA = fx.attacker === 'a';
  const startX = fromA ? '30%' : '70%';
  const endX = fromA ? '68%' : '32%';
  const defX = fromA ? '70%' : '30%';
  const col = fx.color || '#f59e0b';
  const K = fx.kind;
  return (
    <div key={fx.tick} className="absolute inset-0 pointer-events-none overflow-hidden" data-testid={`seat-vfx-${K}`}>
      {K === 'swarm' && (
        <motion.div initial={{ left: startX, opacity: 0 }} animate={{ left: endX, opacity: [0, 1, 1, 0] }} transition={{ duration: 0.6, delay: 0.1 }}
          className="absolute top-[54%] -translate-x-1/2 -translate-y-1/2 flex flex-wrap gap-1 w-24 justify-center">
          {[...Array(14)].map((_, i) => (
            <motion.span key={i} animate={{ y: [0, -8, 0, 7, 0] }} transition={{ duration: 0.35, repeat: 1, delay: i * 0.02 }}
              className="block w-3.5 h-2.5 rounded-full" style={{ background: '#0b0f17', boxShadow: `0 0 8px ${col}` }} />
          ))}
        </motion.div>
      )}
      {K === 'uppercut' && (
        <>
          <motion.div initial={{ opacity: 0, scaleY: 0.3, bottom: '4%' }} animate={{ opacity: [0, 1, 1, 0], scaleY: [0.3, 1.5, 1.8, 1.9], bottom: ['4%', '18%', '42%', '58%'] }} transition={{ duration: 0.6, delay: 0.3 }}
            className="absolute -translate-x-1/2 w-20 h-48 rounded-full" style={{ left: defX, background: `linear-gradient(to top, ${col}, rgba(255,255,255,0.9) 40%, transparent)`, filter: 'blur(1px)', boxShadow: `0 0 30px ${col}` }} />
          <motion.div initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: [0, 1, 0], scale: [0.4, 1.4, 1.6] }} transition={{ duration: 0.45, delay: 0.33 }}
            className="absolute bottom-[26%] -translate-x-1/2 w-28 h-28 rounded-full border-t-4 border-r-4" style={{ left: defX, borderColor: col }} />
        </>
      )}
      {K === 'papers' && (
        <>
          <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: [0, 0.9, 0.5, 0], scale: [0.6, 1.1, 1.15, 1.25] }} transition={{ duration: 0.7 }}
            className="absolute bottom-[14%] -translate-x-1/2 w-36 h-52 rounded-full border-4" style={{ left: startX, borderColor: col, boxShadow: `0 0 26px ${col}` }} />
          {[...Array(7)].map((_, i) => (
            <motion.span key={i} initial={{ left: startX, bottom: '32%', opacity: 0, rotate: 0 }} animate={{ left: endX, opacity: [0, 1, 1, 0], rotate: 360, bottom: ['32%', '48%', '30%'] }} transition={{ duration: 0.55, delay: 0.15 + i * 0.04 }}
              className="absolute -translate-x-1/2 w-4 h-5 bg-white rounded-sm shadow-md" />
          ))}
        </>
      )}
      {K === 'chestplate' && (
        <>
          <motion.div initial={{ opacity: 0.9, scale: 0.2 }} animate={{ opacity: [0.9, 0.4, 0], scale: [0.2, 2.6, 3.2] }} transition={{ duration: 0.6, delay: 0.18 }}
            className="absolute bottom-[32%] -translate-x-1/2 w-40 h-40 rounded-full border-4" style={{ left: startX, borderColor: col, boxShadow: `0 0 34px ${col}` }} />
          <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: [0, 1, 0], scale: [0.6, 1.3, 1.1] }} transition={{ duration: 0.5, delay: 0.1 }}
            className="absolute bottom-[34%] -translate-x-1/2 w-24 h-28 rounded-2xl" style={{ left: startX, background: `radial-gradient(circle, ${col}, transparent 70%)` }} />
        </>
      )}
      {K === 'soundwave' && (
        <>
          {[0, 1, 2].map((i) => (
            <motion.div key={i} initial={{ left: startX, opacity: 0, scale: 0.4 }} animate={{ left: [startX, defX], opacity: [0, 0.85, 0], scale: [0.4, 1, 1.4] }} transition={{ duration: 0.6, delay: 0.15 + i * 0.12 }}
              className="absolute top-[40%] -translate-x-1/2 -translate-y-1/2 w-16 h-28 rounded-full border-4 border-l-transparent" style={{ borderColor: col, transform: `translate(-50%,-50%) scaleX(${fromA ? 1 : -1})` }} />
          ))}
          <motion.span initial={{ opacity: 0, y: 0 }} animate={{ opacity: [0, 1, 0], y: -24 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="absolute top-[26%] -translate-x-1/2 font-black text-xl" style={{ left: defX, color: col }}>z Z z</motion.span>
        </>
      )}
      {K === 'hologram' && [...Array(6)].map((_, i) => (
        <motion.div key={i} initial={{ opacity: 0, scale: 0.82 }} animate={{ opacity: [0, 0.95, 0.4, 0.9, 0], scale: [0.82, 1, 1.05, 1] }} transition={{ duration: 0.85, delay: i * 0.05 }}
          className="absolute rounded-md border-2 flex items-center justify-center overflow-hidden" style={{ left: `${35 + (i % 3) * 15}%`, top: `${24 + Math.floor(i / 3) * 25}%`, width: '58px', height: '82px', borderColor: col, background: 'rgba(8,12,26,0.62)', boxShadow: `0 0 18px ${col}, inset 0 0 12px ${col}66` }}>
          <div className="w-full h-[3px]" style={{ background: col, opacity: 0.8 }} />
        </motion.div>
      ))}
      {K === 'fireball' && (
        <motion.div initial={{ left: startX, opacity: 0, scale: 0.4 }} animate={{ left: [startX, endX, endX], opacity: [0, 1, 1, 0], scale: [0.4, 1, 1.1, 1.7] }} transition={{ duration: 0.5, delay: 0.12, times: [0, 0.12, 0.75, 1] }}
          className="absolute top-[46%] -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full" style={{ background: `radial-gradient(circle, #fff, ${col} 55%, rgba(0,0,0,0) 72%)`, boxShadow: `0 0 30px ${col}` }} />
      )}
      {K === 'beam' && (
        <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: [0, 1, 1, 0], scaleX: [0, 1, 1, 1] }} transition={{ duration: 0.45, delay: 0.28 }}
          className="absolute top-[42%] h-3 rounded-full" style={{ left: fromA ? '30%' : 'auto', right: fromA ? 'auto' : '30%', width: '40%', background: `linear-gradient(${fromA ? '90deg' : '270deg'}, ${col}, #fff)`, boxShadow: `0 0 24px ${col}`, transformOrigin: fromA ? 'left' : 'right' }} />
      )}
      {K === 'orbital' && (
        <motion.div initial={{ top: '-12%', opacity: 0, scaleX: 0.3 }} animate={{ top: ['-12%', '36%', '36%'], opacity: [0, 1, 0], scaleX: [0.3, 1, 1.5] }} transition={{ duration: 0.5, delay: 0.25 }}
          className="absolute -translate-x-1/2 w-10 h-56 rounded-full" style={{ left: defX, background: `linear-gradient(to bottom, rgba(0,0,0,0), ${col}, #fff)`, boxShadow: `0 0 30px ${col}` }} />
      )}
      {K === 'tornado' && (
        <motion.div initial={{ opacity: 0, rotate: 0, scale: 0.4 }} animate={{ opacity: [0, 1, 1, 0], rotate: 720, scale: [0.4, 1, 1.1] }} transition={{ duration: 0.65, delay: 0.2 }}
          className="absolute bottom-[12%] -translate-x-1/2 w-28 h-52" style={{ left: defX, borderRadius: '50%', background: `conic-gradient(from 0deg, rgba(0,0,0,0), ${col}, rgba(0,0,0,0), ${col}, rgba(0,0,0,0))`, filter: 'blur(1px)' }} />
      )}
      {K === 'fog' && [0, 1, 2].map((i) => (
        <motion.div key={i} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: [0, 0.6, 0], scale: [0.4, 1.6, 2.1] }} transition={{ duration: 0.85, delay: 0.2 + i * 0.1 }}
          className="absolute bottom-[14%] -translate-x-1/2 rounded-full" style={{ left: `${fromA ? 60 + i * 4 : 40 - i * 4}%`, width: '92px', height: '92px', background: `radial-gradient(circle, ${col}cc, rgba(0,0,0,0) 70%)` }} />
      ))}
      {K === 'wall' && (
        <motion.div initial={{ opacity: 0, scaleY: 0.2 }} animate={{ opacity: [0, 0.85, 0.75, 0], scaleY: [0.2, 1, 1, 1] }} transition={{ duration: 0.7, delay: 0.1 }}
          className="absolute bottom-[12%] -translate-x-1/2 w-12 h-48 rounded-lg border-2" style={{ left: fromA ? '46%' : '54%', borderColor: col, background: `${col}33`, boxShadow: `0 0 24px ${col}` }} />
      )}
      {K === 'wave' && (
        <motion.div initial={{ left: startX, opacity: 0 }} animate={{ left: [startX, defX], opacity: [0, 0.85, 0] }} transition={{ duration: 0.5, delay: 0.18 }}
          className="absolute bottom-[10%] -translate-x-1/2 w-44 h-44 rounded-t-full" style={{ background: `linear-gradient(to top, ${col}, rgba(0,0,0,0))`, filter: 'blur(1px)' }} />
      )}
      {K === 'flash' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0, 0.92, 0] }} transition={{ duration: 0.4, delay: 0.28 }}
          className="absolute inset-0" style={{ background: `radial-gradient(circle at ${defX} 45%, #fff, ${col} 42%, rgba(0,0,0,0) 76%)` }} />
      )}
      {fx.prop && (() => {
        const img = P(fx.prop);
        const base = fromA ? 54 : 18;
        if (fx.pstyle === 'drop') return (<>
          <motion.img src={img} alt="" initial={{ top: '-85%', opacity: 0, rotate: -5 }} animate={{ top: ['-85%', '2%', '0%'], opacity: [0, 1, 1], rotate: [-5, 3, 0] }} transition={{ duration: 0.5, delay: 0.18, ease: 'easeIn' }}
            className="absolute -translate-x-1/2 w-auto h-[86%] object-contain object-bottom drop-shadow-2xl" style={{ left: defX, zIndex: 6 }} />
          <motion.div initial={{ opacity: 0, scaleX: 0.3 }} animate={{ opacity: [0, 0.95, 0], scaleX: [0.3, 1.8, 2.3] }} transition={{ duration: 0.4, delay: 0.55 }}
            className="absolute bottom-[7%] -translate-x-1/2 w-44 h-8 rounded-full" style={{ left: defX, background: `radial-gradient(ellipse, #fff, ${col} 45%, rgba(0,0,0,0) 72%)`, zIndex: 6 }} />
        </>);
        if (fx.pstyle === 'rain') return (<>{[...Array(9)].map((_, i) => (
          <motion.img key={i} src={img} alt="" initial={{ top: '-28%', opacity: 0 }} animate={{ top: ['-28%', '58%'], opacity: [0, 1, 1, 0] }} transition={{ duration: 0.75, delay: 0.08 + i * 0.07, ease: 'easeIn' }}
            className="absolute w-16 h-16 object-contain drop-shadow-lg" style={{ left: `${base + (i % 5) * 8}%`, zIndex: 6 }} />))}</>);
        if (fx.pstyle === 'throw') return (
          <motion.img src={img} alt="" initial={{ left: startX, opacity: 0, rotate: 0, scale: 0.7 }} animate={{ left: [startX, defX], opacity: [0, 1, 1, 0], rotate: fromA ? 540 : -540, scale: [0.7, 1.25, 1.4] }} transition={{ duration: 0.5, delay: 0.14 }}
            className="absolute top-[40%] -translate-x-1/2 -translate-y-1/2 w-32 h-32 object-contain drop-shadow-xl" style={{ zIndex: 6 }} />
        );
        if (fx.pstyle === 'shield') return (
          <motion.img src={img} alt="" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1.2, 1] }} transition={{ duration: 0.8, delay: 0.06 }}
            className="absolute bottom-[12%] -translate-x-1/2 w-auto h-[62%] object-contain" style={{ left: fromA ? '44%' : '56%', filter: `drop-shadow(0 0 18px ${col})`, zIndex: 6 }} />
        );
        if (fx.pstyle === 'grow') return (
          <motion.img src={img} alt="" initial={{ opacity: 0, scaleY: 0.15, y: 50 }} animate={{ opacity: [0, 1, 1], scaleY: [0.15, 1.35, 1.45], y: 0 }} transition={{ duration: 0.55, delay: 0.18 }}
            className="absolute bottom-[10%] -translate-x-1/2 w-auto h-[70%] object-contain origin-bottom" style={{ left: defX, zIndex: 6 }} />
        );
        if (fx.pstyle === 'cloud') return (
          <motion.img src={img} alt="" initial={{ opacity: 0, y: 34, scale: 0.6 }} animate={{ opacity: [0, 1, 1, 0.7], y: 0, scale: [0.6, 1.4, 1.3] }} transition={{ duration: 0.6, delay: 0.18 }}
            className="absolute bottom-[14%] -translate-x-1/2 w-auto h-[54%] object-contain" style={{ left: defX, zIndex: 6 }} />
        );
        return null;
      })()}
    </div>
  );
};

const HPBar = ({ hp, color, name, align }) => (
  <div className="flex-1 min-w-0">
    <div className="flex items-center gap-1 mb-0.5" style={{ flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
      <span className="text-xs font-black truncate" style={{ color }}>{name}</span>
      <span className="text-[10px] text-white/50">{Math.round(hp)}</span>
    </div>
    <div className={`h-4 w-full bg-black/60 rounded-md overflow-hidden flex border ${align === 'right' ? 'justify-end' : ''} ${hp <= 30 ? 'border-red-500 animate-pulse' : 'border-white/15'}`}>
      <motion.div animate={{ width: `${hp}%` }} transition={{ type: 'spring', stiffness: 130, damping: 18 }}
        className="h-full" style={{ background: hp > 30 ? color : '#ef4444' }} />
    </div>
  </div>
);

const SeatFighterPage = () => {
  const navigate = useNavigate();
  const playerId = useRef(getPlayerId()).current;
  const [duels, setDuels] = useState([]);
  const [closed, setClosed] = useState([]);
  const [view, setView] = useState('arena');
  const [active, setActive] = useState(null);
  const [me, setMe] = useState({ credits: 0, next_move_free: true });
  const [sel, setSel] = useState({ side: null, power: null });
  const [casting, setCasting] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundIntro, setRoundIntro] = useState(null);
  const [soundOn, setSoundOn] = useState(true);

  const fetchArena = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        axios.get(`${API}/seat/duels`),
        axios.get(`${API}/seat/me?player_id=${playerId}`),
      ]);
      setDuels(d.data.duels || []);
      setClosed(d.data.closed || []);
      setMe(m.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [playerId]);

  const refreshMe = useCallback(async () => {
    try { const m = await axios.get(`${API}/seat/me?player_id=${playerId}`); setMe(m.data); } catch (e) {}
  }, [playerId]);

  useEffect(() => {
    fetchArena();
    axios.get(`${API}/seat/packages`).then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, [fetchArena]);

  // Handle Stripe return
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const pay = p.get('sf_payment');
    const cs = p.get('cs');
    if (pay === 'cancelled') {
      toast('Purchase cancelled.');
      window.history.replaceState({}, '', '/seat-fighter');
    } else if (pay === 'stripe' && cs) {
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await axios.get(`${API}/payments/stripe/status/${cs}`);
          if (r.data.payment_status === 'paid') {
            toast.success('Moves added — back to the fight! 🥊');
            await refreshMe();
            window.history.replaceState({}, '', '/seat-fighter');
            return;
          }
        } catch (e) {}
        if (tries < 10) setTimeout(poll, 1500);
        else window.history.replaceState({}, '', '/seat-fighter');
      };
      toast.loading('Confirming your purchase…', { duration: 2000 });
      poll();
    }
  }, [refreshMe]);

  const openDuel = async (slug) => {
    setSel({ side: null, power: null });
    // Start audio synchronously INSIDE the tap gesture (mobile autoplay policy).
    initAudio();
    setMuted(!soundOn);
    setMusicTheme(slug);
    if (soundOn) startMusic();
    try {
      const r = await axios.get(`${API}/seat/duels/${slug}?player_id=${playerId}`);
      setActive(r.data);
      votesRef.current = { a: r.data.votes_a || 0, b: r.data.votes_b || 0 };
      if (r.data.me) setMe((m) => ({ ...m, ...r.data.me }));
      setView('duel');
      window.scrollTo(0, 0);
    } catch (e) { toast.error('Could not open duel.'); }
  };

  const aCtrl = useAnimationControls();
  const bCtrl = useAnimationControls();
  const stageCtrl = useAnimationControls();
  const votesRef = useRef({ a: 0, b: 0 });
  const [fx, setFx] = useState({ on: false, attacker: null, defender: null, kind: null, color: null, react: null, text: '', tick: 0, spark: false, prop: null, pstyle: null });
  const [liveHit, setLiveHit] = useState(null);
  const DMG = 3;
  const hpA = Math.max(0, 100 - (active?.votes_b || 0) * DMG);
  const hpB = Math.max(0, 100 - (active?.votes_a || 0) * DMG);
  const koSide = active ? (hpA <= 0 ? 'a' : hpB <= 0 ? 'b' : null) : null;

  const startIdle = useCallback((ctrl, dir) => {
    ctrl.start({
      x: [0, dir * 11, 0, dir * 5, 0], scale: 1, rotate: 0, y: 0, filter: 'brightness(1)',
      transition: { duration: 3.4, repeat: Infinity, ease: 'easeInOut' },
    });
  }, []);

  const runMotion = (atk, dir, motion) => {
    switch (motion) {
      case 'leap': return atk.start({ x: [0, -dir * 14, dir * 52, 0], y: [0, -18, -78, 0], scale: [1, 1.05, 1.2, 1], transition: { duration: 0.7, times: [0, 0.2, 0.55, 1] } });
      case 'guard': return atk.start({ x: [0, -dir * 16, -dir * 6, 0], scale: [1, 1.04, 1.04, 1], transition: { duration: 0.8 } });
      case 'puff': return atk.start({ x: [0, -dir * 8, 0], scale: [1, 1.22, 1.08, 1], transition: { duration: 0.7 } });
      case 'cast': return atk.start({ x: [0, dir * 16, 0], scale: [1, 1.05, 1], transition: { duration: 0.8 } });
      case 'multiply': return atk.start({ scale: [1, 1.1, 0.96, 1], transition: { duration: 0.7 } });
      case 'dash':
      default: return atk.start({ x: [0, -dir * 24, dir * 100, 0], scale: [1, 1.05, 1.18, 1], transition: { duration: 0.75, times: [0, 0.18, 0.5, 1], ease: 'easeOut' } });
    }
  };

  // Living idle footwork + round intro when entering a duel
  useEffect(() => {
    if (view !== 'duel' || !active) return;
    startIdle(aCtrl, 1);
    startIdle(bCtrl, -1);
    setRoundIntro('round');
    const t1 = setTimeout(() => setRoundIntro('fight'), 1050);
    const t2 = setTimeout(() => setRoundIntro(null), 2050);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, active?.slug]);

  const playFight = (side, powerId, powerName) => {
    if (koSide) return;
    const dir = side === 'a' ? 1 : -1;
    const atk = side === 'a' ? aCtrl : bCtrl;
    const def = side === 'a' ? bCtrl : aCtrl;
    const defSide = side === 'a' ? 'b' : 'a';
    const defDir = defSide === 'a' ? 1 : -1;
    const fallbackColor = side === 'a' ? active.fighter_a.color : active.fighter_b.color;
    const mv = MOVES[`${active.slug}:${side}:${powerId}`] || { color: fallbackColor, motion: 'dash', vfx: 'punch', react: 'knockback' };

    setFx({ on: true, attacker: side, defender: null, kind: mv.vfx, color: mv.color, react: mv.react, text: powerName, tick: Date.now(), spark: false, prop: mv.prop || null, pstyle: mv.pstyle || null });
    runMotion(atk, dir, mv.motion);
    playSfx(mv.vfx);

    const contactAt = mv.motion === 'dash' ? 360 : 430;
    // contact: effect impact + shake + reaction + hurt pose
    setTimeout(() => {
      setFx((p) => ({ ...p, defender: defSide, spark: true }));
      playSfx('hit');
      stageCtrl.start({ x: [0, -10, 10, -6, 5, 0], transition: { duration: 0.36 } });
      if (mv.react === 'launch') {
        def.start({ y: [0, -58, 0], x: [0, dir * 34, 0], filter: ['brightness(1)', 'brightness(3)', 'brightness(1.2)'], transition: { duration: 0.5 } });
      } else if (mv.react === 'doze') {
        def.start({ rotate: [0, 8, -5, 8, -3, 0], filter: ['brightness(1)', 'brightness(1.6)', 'brightness(1)'], transition: { duration: 0.65 } });
      } else {
        def.start({ x: [0, dir * 64, dir * 30, 0], filter: ['brightness(1)', 'brightness(3) saturate(1.6)', 'brightness(1.2)'], transition: { duration: 0.6 } });
      }
    }, contactAt);
    setTimeout(() => setFx((p) => ({ ...p, spark: false })), contactAt + 280);
    // fighters recover to idle, but keep the prop/effect on screen a touch longer
    setTimeout(() => {
      setFx((p) => ({ ...p, attacker: null, defender: null }));
      startIdle(atk, dir);
      startIdle(def, defDir);
    }, 1050);
    setTimeout(() => setFx((p) => ({ ...p, on: false })), 1550);
  };

  // Music lifecycle + KO sound
  useEffect(() => {
    if (view === 'duel') { if (soundOn) startMusic(); }
    else stopMusic();
  }, [view, soundOn]);
  useEffect(() => () => stopMusic(), []);

  // A hit landed by another player (or by us) — flash the fighter losing HP.
  const remoteHit = useCallback((damagedSide) => {
    const def = damagedSide === 'a' ? aCtrl : bCtrl;
    const dir = damagedSide === 'a' ? 1 : -1;
    playSfx('hit');
    stageCtrl.start({ x: [0, -8, 8, -4, 0], transition: { duration: 0.3 } });
    def.start({ x: [0, dir * 40, dir * 16, 0], filter: ['brightness(1)', 'brightness(2.6) saturate(1.5)', 'brightness(1.1)'], transition: { duration: 0.5 } })
      .then(() => startIdle(def, -dir));
    setLiveHit({ side: damagedSide, tick: Date.now() });
    setTimeout(() => setLiveHit((p) => (p && Date.now() - p.tick >= 900 ? null : p)), 950);
  }, [aCtrl, bCtrl, stageCtrl, startIdle]);

  // Real-time: poll the shared duel so other players' hits appear live.
  useEffect(() => {
    if (view !== 'duel' || !active?.slug) return undefined;
    const slug = active.slug;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/seat/duels/${slug}?player_id=${playerId}`);
        const na = r.data.votes_a || 0;
        const nb = r.data.votes_b || 0;
        const prev = votesRef.current;
        const dA = na - prev.a; // extra A→B hits since we last saw
        const dB = nb - prev.b; // extra B→A hits since we last saw
        if (dA > 0 || dB > 0) {
          votesRef.current = { a: na, b: nb };
          setActive((d) => (d ? { ...d, votes_a: na, votes_b: nb, total_votes: na + nb, status: r.data.status } : d));
          if (dA > 0) remoteHit('b');
          if (dB > 0) remoteHit('a');
        } else if (r.data.status !== active.status) {
          setActive((d) => (d ? { ...d, status: r.data.status } : d));
        }
      } catch (e) { /* noop */ }
    }, 2500);
    return () => clearInterval(id);
  }, [view, active?.slug, active?.status, playerId, remoteHit]);

  useEffect(() => {
    if (koSide) playSfx('ko');
  }, [koSide]);

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      setMuted(!next);
      if (next && view === 'duel') startMusic();
      else stopMusic();
      return next;
    });
  };

  const rematch = async () => {
    try {
      await axios.post(`${API}/seat/duels/${active.slug}/rematch`);
      setActive((d) => ({ ...d, votes_a: 0, votes_b: 0, total_votes: 0, ko: null, status: 'active' }));
      votesRef.current = { a: 0, b: 0 };
      setSel({ side: null, power: null });
      setRoundIntro('round');
      setTimeout(() => setRoundIntro('fight'), 1050);
      setTimeout(() => setRoundIntro(null), 2050);
      toast('Rematch! Fighters reset to full health 🥊');
    } catch (e) { toast.error('Could not restart the fight.'); }
  };

  const castMove = async () => {
    if (!sel.side || !sel.power) { toast.error('Pick a fighter and a superpower first.'); return; }
    setCasting(true);
    try {
      const r = await axios.post(`${API}/seat/duels/${active.slug}/move`, {
        player_id: playerId, side: sel.side, power_id: sel.power,
      });
      setActive((d) => ({ ...d, votes_a: r.data.votes_a, votes_b: r.data.votes_b, total_votes: r.data.total_votes }));
      votesRef.current = { a: r.data.votes_a, b: r.data.votes_b };
      setMe((m) => ({ ...m, credits: r.data.credits, next_move_free: r.data.next_move_free, free_move_used_today: true }));
      playFight(sel.side, sel.power, r.data.power_name);
      setSel({ side: null, power: null });
      toast.success(`${r.data.power_name}! ${r.data.fighter_name} lands a hit${r.data.free ? ' (free move)' : ''} 🥊`);
    } catch (e) {
      if (e.response?.status === 402) { setBuyOpen(true); toast.error(e.response.data.detail); }
      else toast.error(e.response?.data?.detail || 'Move failed.');
    } finally { setCasting(false); }
  };

  const buyPack = async (pkgId) => {
    try {
      const r = await axios.post(`${API}/seat/checkout`, {
        player_id: playerId, package_id: pkgId, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout.'); }
  };

  const CreditBadge = () => (
    <button onClick={() => setBuyOpen(true)} data-testid="seat-credits-badge"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-sm font-bold hover:bg-amber-500/25 transition-colors">
      <Coins className="w-4 h-4" /> {me.credits} moves
    </button>
  );

  return (
    <div className="min-h-screen text-white" style={{ background: 'radial-gradient(1200px 600px at 50% -10%, #3b0a0a 0%, #120407 45%, #09060a 100%)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <style>{`
          @keyframes sfBobA { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
          @keyframes sfBobB { 0%,100% { transform: translateY(0) scaleX(-1); } 50% { transform: translateY(-7px) scaleX(-1); } }
          .sf-bob-a { animation: sfBobA 2.6s ease-in-out infinite; }
          .sf-bob-b { animation: sfBobB 2.9s ease-in-out infinite; }
          .sf-spark { position: relative; width: 92px; height: 92px; border-radius: 9999px;
            background: radial-gradient(circle, #fff 0%, #fde68a 28%, #f59e0b 52%, rgba(245,158,11,0) 72%);
            box-shadow: 0 0 42px 12px rgba(245,158,11,0.85); }
          .sf-spark::before { content: ''; position: absolute; inset: -16px;
            background: conic-gradient(from 10deg, transparent 0 7%, #fff 7% 11%, transparent 11% 24%, #fde68a 24% 28%, transparent 28% 49%, #fff 49% 53%, transparent 53% 74%, #fde68a 74% 78%, transparent 78% 100%);
            -webkit-mask: radial-gradient(circle, #000 32%, transparent 60%); mask: radial-gradient(circle, #000 32%, transparent 60%); }
        `}</style>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => (view === 'duel' ? (setView('arena'), fetchArena()) : navigate('/'))}
            data-testid="seat-back-btn"
            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
            {view === 'duel' ? <><ArrowLeft className="w-4 h-4" /> Arena</> : <><Home className="w-4 h-4" /> Almanac</>}
          </button>
          <div className="flex items-center gap-2">
            <CreditBadge />
            <button onClick={toggleSound} data-testid="seat-sound-toggle" aria-label="Toggle sound"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors">
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="text-center mb-6">
          <img src={LOGO} alt="Seat Fighter" className="mx-auto w-56 sm:w-72 rounded-2xl" data-testid="seat-logo" />
          <p className="text-white/60 text-sm mt-2">Every leader's fighting for a seat. Pick a side, land a superpower, decide who falls.</p>
          <p className="text-amber-300/80 text-xs mt-1">3 free moves a day · extra moves cost 1 credit each</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <a href={`https://wa.me/?text=${encodeURIComponent('🥊 Seat Fighter — pick a side & land a superpower! ' + API + '/share/seat-fighter')}`} target="_blank" rel="noopener noreferrer" data-testid="seat-share-whatsapp"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600/90 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors">
              <Share2 className="w-3.5 h-3.5" /> Share on WhatsApp
            </a>
            <button onClick={() => { try { navigator.clipboard.writeText(`${API}/share/seat-fighter`); toast('Link copied — paste it anywhere!'); } catch (e) { toast(`${API}/share/seat-fighter`); } }} data-testid="seat-share-copy"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-white/80 text-xs font-semibold transition-colors">
              <LinkIcon className="w-3.5 h-3.5" /> Copy link
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-red-400" /></div>
        ) : view === 'arena' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="seat-arena">
              {duels.map((d) => (
                <motion.button key={d.slug} whileHover={{ y: -4 }} onClick={() => openDuel(d.slug)}
                  data-testid={`seat-duel-card-${d.slug}`}
                  className="rounded-2xl border border-white/10 bg-black/40 p-4 text-left hover:border-red-400/40 transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-red-400/80 mb-2 font-bold">{d.title}</p>
                  <div className="flex items-stretch gap-2 mb-3">
                    <div className="flex-1 flex flex-col items-center min-w-0">
                      {d.fighter_a.image_url && <img src={d.fighter_a.image_url} alt={d.fighter_a.name} className="w-full aspect-square object-cover rounded-xl border" style={{ borderColor: `${d.fighter_a.color}66` }} />}
                      <span className="font-bold text-sm leading-tight text-center mt-1.5 truncate w-full" style={{ color: d.fighter_a.color }}>{d.fighter_a.name}</span>
                    </div>
                    <div className="flex items-center"><span className="shrink-0 text-xs font-black text-white/40">VS</span></div>
                    <div className="flex-1 flex flex-col items-center min-w-0">
                      {d.fighter_b.image_url && <img src={d.fighter_b.image_url} alt={d.fighter_b.name} className="w-full aspect-square object-cover rounded-xl border" style={{ borderColor: `${d.fighter_b.color}66` }} />}
                      <span className="font-bold text-sm leading-tight text-center mt-1.5 truncate w-full" style={{ color: d.fighter_b.color }}>{d.fighter_b.name}</span>
                    </div>
                  </div>
                  <TallyBar a={d.votes_a} b={d.votes_b} colorA={d.fighter_a.color} colorB={d.fighter_b.color} />
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[11px] text-white/50">{d.total_votes} hits landed</span>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-red-300"><Swords className="w-3.5 h-3.5" /> Enter the ring</span>
                  </div>
                </motion.button>
              ))}
            </div>

            {closed.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-bold text-white/60 mb-3 flex items-center gap-1.5"><Trophy className="w-4 h-4 text-amber-400" /> Past KOs</h3>
                <div className="space-y-2">
                  {closed.map((d) => (
                    <div key={d.slug} className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`seat-closed-${d.slug}`}>
                      <p className="text-xs text-white/50">{d.title}</p>
                      <p className="text-sm font-bold text-amber-300">🏆 {d.ko?.winner_name} won</p>
                      {d.ko?.narration && <p className="text-xs text-white/60 mt-1 italic">{d.ko.narration}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : active ? (
          <div data-testid="seat-duel-view" className="pb-28">
            <p className="text-center text-[11px] uppercase tracking-[0.25em] text-red-400/80 font-bold mb-3">{active.title}</p>

            {/* Twin HP bars */}
            <div className="flex items-center gap-2 mb-2">
              <HPBar hp={hpA} color={active.fighter_a.color} name={active.fighter_a.name} align="left" />
              <span className="text-white/40 font-black text-xs shrink-0">VS</span>
              <HPBar hp={hpB} color={active.fighter_b.color} name={active.fighter_b.name} align="right" />
            </div>

            {/* Fight stage */}
            <motion.div animate={stageCtrl} className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: '16 / 10' }} data-testid="seat-fight-stage">
              <img src={stageBg(active.slug)} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/20" />

              {(() => {
                const pa = poseSet(active.slug, 'a', active.fighter_a);
                const pb = poseSet(active.slug, 'b', active.fighter_b);
                const aSrc = fx.attacker === 'a' ? pa.atk : fx.defender === 'a' ? pa.hurt : pa.idle;
                const bSrc = fx.attacker === 'b' ? pb.atk : fx.defender === 'b' ? pb.hurt : pb.idle;
                return (
                  <>
                    <motion.div animate={aCtrl} className="absolute bottom-[5%] left-0 sm:left-8 h-[84%] w-[48%] flex items-end justify-center pointer-events-none">
                      <img src={aSrc} alt={active.fighter_a.name} className="sf-bob-a h-full w-auto max-w-full object-contain object-bottom drop-shadow-[0_12px_14px_rgba(0,0,0,0.6)]"
                        style={{ filter: koSide === 'a' ? 'grayscale(1) brightness(0.45)' : undefined }} />
                    </motion.div>
                    <motion.div animate={bCtrl} className="absolute bottom-[5%] right-0 sm:right-8 h-[84%] w-[48%] flex items-end justify-center pointer-events-none">
                      <img src={bSrc} alt={active.fighter_b.name} className="sf-bob-b h-full w-auto max-w-full object-contain object-bottom drop-shadow-[0_12px_14px_rgba(0,0,0,0.6)]"
                        style={{ filter: koSide === 'b' ? 'grayscale(1) brightness(0.45)' : undefined }} />
                    </motion.div>
                  </>
                );
              })()}

              {/* Signature move effects */}
              <AnimatePresence>
                <MoveFX fx={fx} />
              </AnimatePresence>

              {/* Impact spark */}
              <AnimatePresence>
                {fx.spark && (
                  <motion.div key={`spark-${fx.tick}`} initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1.2 }} exit={{ opacity: 0, scale: 1.6 }}
                    transition={{ duration: 0.22 }} className="absolute top-[42%] -translate-y-1/2 -translate-x-1/2 pointer-events-none"
                    style={{ left: fx.defender === 'a' ? '30%' : '70%' }}>
                    <div className="sf-spark" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Power-name flare */}
              <AnimatePresence>
                {fx.attacker && (
                  <motion.div key={`txt-${fx.tick}`} initial={{ opacity: 0, y: 14, scale: 0.7 }} animate={{ opacity: 1, y: -14, scale: 1.1 }} exit={{ opacity: 0 }}
                    className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/80 border border-white/10 text-sm font-black whitespace-nowrap"
                    style={{ color: fx.attacker === 'a' ? active.fighter_a.color : active.fighter_b.color }}>
                    {fx.text}!
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Live hit from another player */}
              <AnimatePresence>
                {liveHit && (
                  <motion.div key={`live-${liveHit.tick}`} initial={{ opacity: 0, y: 10, scale: 0.6 }} animate={{ opacity: 1, y: -10, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 0.3 }} className="absolute top-[30%] -translate-x-1/2 px-2.5 py-1 rounded-full bg-red-600/90 border border-white/30 text-[11px] font-black text-white whitespace-nowrap pointer-events-none"
                    style={{ left: liveHit.side === 'a' ? '28%' : '72%' }} data-testid="seat-live-hit">
                    ⚡ −{DMG} HP · live
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Round intro */}
              <AnimatePresence>
                {roundIntro && (
                  <motion.div key={roundIntro} initial={{ opacity: 0, scale: 1.5 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.4 }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="seat-round-intro">
                    <span className={`font-black italic drop-shadow-[0_3px_10px_rgba(0,0,0,0.9)] ${roundIntro === 'fight' ? 'text-6xl sm:text-7xl text-red-500' : 'text-4xl sm:text-5xl text-white'}`}>
                      {roundIntro === 'fight' ? 'FIGHT!' : 'ROUND 1'}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* KO overlay */}
              {(koSide || (active.status !== 'active' && active.ko)) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/65 px-6 text-center" data-testid="seat-ko-overlay">
                  <p className="font-black text-5xl sm:text-6xl text-red-500 italic drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]">K.O.</p>
                  <p className="font-bold text-amber-300 mt-1">{active.ko?.winner_name || (koSide === 'a' ? active.fighter_b.name : active.fighter_a.name)} wins the seat!</p>
                  {active.ko?.narration && <p className="text-xs text-white/70 mt-2 italic">{active.ko.narration}</p>}
                  {koSide && active.status === 'active' && (
                    <button onClick={rematch} data-testid="seat-rematch-btn"
                      className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-orange-500 text-white font-black tracking-wide shadow-xl hover:scale-[1.03] transition-transform">
                      <RotateCcw className="w-5 h-5" /> REMATCH
                    </button>
                  )}
                </div>
              )}
            </motion.div>

            {/* Move controls */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              {['a', 'b'].map((side) => {
                const f = side === 'a' ? active.fighter_a : active.fighter_b;
                return (
                  <div key={side}>
                    <p className="font-black text-sm mb-1.5 truncate" style={{ color: f.color }}>{f.name}</p>
                    <div className="space-y-1.5">
                      {f.powers.map((p) => (
                        <PowerButton key={p.id} power={p} color={f.color}
                          selected={sel.side === side && sel.power === p.id}
                          onClick={() => setSel({ side, power: p.id })}
                          testid={`seat-power-${side}-${p.id}`} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {active.status === 'active' && !koSide && (
              <div className="sticky bottom-4 mt-4">
                <button onClick={castMove} disabled={casting || !sel.power}
                  data-testid="seat-cast-btn"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-red-600 to-orange-500 text-white font-black text-lg tracking-wide shadow-xl hover:scale-[1.01] transition-transform disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2">
                  {casting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Swords className="w-5 h-5" />}
                  {!sel.power ? 'PICK A FIGHTER & MOVE' : me.next_move_free ? 'FIGHT! · free move' : `FIGHT! · 1 credit (${me.credits} left)`}
                </button>
                {!me.next_move_free && me.credits <= 0 && (
                  <p className="text-center text-xs text-amber-300/80 mt-2">Out of moves — <button onClick={() => setBuyOpen(true)} className="underline font-bold" data-testid="seat-buy-link">grab a credit pack</button></p>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Buy credits modal */}
      <AnimatePresence>
        {buyOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setBuyOpen(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/80 backdrop-blur-sm" data-testid="seat-buy-modal">
            <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-3xl p-6 max-w-sm w-full border border-amber-400/30 bg-zinc-950 relative text-center">
              <button onClick={() => setBuyOpen(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="seat-buy-close"><X className="w-5 h-5" /></button>
              <Coins className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <h3 className="font-black text-xl mb-1">Buy more moves</h3>
              <p className="text-sm text-white/60 mb-5">Each credit = one extra move in any duel. Your first move each day is always free.</p>
              <div className="space-y-2">
                {packages.map((pk) => (
                  <button key={pk.id} onClick={() => buyPack(pk.id)} data-testid={`seat-pack-${pk.id}`}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-400/30 hover:bg-amber-500/20 transition-colors">
                    <span className="font-bold">{pk.credits} moves</span>
                    <span className="text-amber-300 font-black">${pk.amount.toFixed(2)}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-4">Secure checkout via Stripe.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SeatFighterPage;
